import "./TableCell.scss"

import classNames from "classnames"
import React from "react"

import Avatar from "../Avatar"
import Checkbox from "../Checkbox"
import InputButton from "../InputButton"
import ProgressBarCell from "../ProgressBarCell"
import RadioButton from "../RadioButton"
import Stat from "../Stat"
import Status from "../Status"
import SvgIcon from "../SvgIcon"
import Switch from "../Switch"
import WeekDayStatusCell from "../WeekDayStatusCell"

export const CELL_VARIANT = {
  CHECKBOX: "checkbox",
  RADIO: "radio",
  ICON: "icon",
  TOGGLE: "toggle",
  TEXT: "text",
  STAT: "stat",
  STATUS: "status",
  WEEKDAYS: "weekdays",
  PROGRESS: "progress",
  STEPPER: "stepper",
  LINK: "link",
  AVATAR: "avatar",
}

const TableCell = ({
  cellType,
  tableCellWrapperClassName,
  setTooltip,
  closeTooltip,
  onChange,
  iconClassname,
  iconOnClick,
  // Checkbox props
  checkboxIcon,
  isCheckboxChecked,
  checkboxOnClick,
  isCheckboxDisabled,
  // Radio props
  isRadioButtonChecked,
  isRadioButtonDisabled,
  radioButtonName,
  // Icon props
  icon,
  // Toggle props
  switchInitChecked,
  switchDisabled = false,
  labelType,
  // Text props
  text,
  helperText,
  link,
  textCellIcon,
  tooltipText,
  inputButton,
  // Stat props
  statColor,
  statShowPercentage,
  // Status props
  statusIcon,
  statusColor,
  statusTooltip,
  // Progress bar props
  progressPercentage,
  progressTotal,
  // Weekday props
  weekdayIntervals,
  className,
  // Avatar props
  name,
  imgSource,
  isAllowedRedirection = false,
  LinkComponent,
  tt,
}) => {
  const textTableCellClassNames = classNames(`large`, {
    "cell-link": link,
    "cell-text": !link,
    "text-wrap-no-wrap": helperText,
  })

  const renderInnerContent = () => {
    switch (cellType) {
      case CELL_VARIANT.CHECKBOX:
        return (
          <Checkbox
            icon={checkboxIcon}
            checked={isCheckboxChecked}
            onClick={checkboxOnClick}
            disabled={isCheckboxDisabled}
          />
        )
      case CELL_VARIANT.RADIO:
        return (
          <RadioButton
            checked={isRadioButtonChecked}
            disabled={isRadioButtonDisabled}
            name={radioButtonName}
            onChange={onChange}
          />
        )
      case CELL_VARIANT.ICON:
        return (
          <SvgIcon
            icon={icon}
            className="table-cell-expand-icon"
            spanClassName="table-cell-expand-span"
          />
        )
      case CELL_VARIANT.TOGGLE:
        return (
          <Switch
            labelType={labelType}
            initChecked={switchInitChecked}
            switchContainerClassName="switch-container-main-in-table"
            disabled={switchDisabled}
            onChange={onChange}
            tt={tt}
          />
        )
      case CELL_VARIANT.TEXT:
        return (
          <div className="cell-content">
            {textCellIcon && (
              <SvgIcon
                icon={textCellIcon}
                className="cell-content-icon"
                spanClassName="cell-content-icon-span"
              />
            )}
            <div>
              <div
                onMouseEnter={e => {
                  if (tooltipText) {
                    setTooltip({
                      event: e,
                      text: tooltipText,
                      replaceTopWithBottom: true,
                    })
                  }
                }}
                onMouseLeave={() => {
                  if (tooltipText) {
                    closeTooltip()
                  }
                }}
              >
                {link ? (
                  <LinkComponent
                    to={isAllowedRedirection ? link : "#"}
                    className={textTableCellClassNames}
                  >
                    {text}
                  </LinkComponent>
                ) : (
                  <div className={textTableCellClassNames}>{text}</div>
                )}
              </div>
              <div className="cell-helper-text small">{helperText}</div>
            </div>
            {inputButton && (
              <InputButton icon={icon} className={iconClassname} onClick={iconOnClick} />
            )}
          </div>
        )
      case CELL_VARIANT.STAT:
        return (
          <Stat
            text={text}
            color={statColor}
            className={className}
            statShowPercentage={statShowPercentage}
          />
        )
      case CELL_VARIANT.STATUS:
        return (
          <Status
            icon={statusIcon}
            text={text}
            color={statusColor}
            statusTooltip={statusTooltip}
            setTooltip={setTooltip}
            closeTooltip={closeTooltip}
          />
        )
      case CELL_VARIANT.WEEKDAYS:
        return (
          <WeekDayStatusCell
            intervals={weekdayIntervals}
            setTooltip={setTooltip}
            closeTooltip={closeTooltip}
            tt={tt}
          />
        )
      case CELL_VARIANT.PROGRESS:
        return <ProgressBarCell progress={progressPercentage} total={progressTotal} />
      case CELL_VARIANT.STEPPER:
        return (
          <div className="cell-stepper">
            <div className="cell-stepper-icon-wrapper">
              <SvgIcon icon="minus-20" className="cell-stepper-icon" />
            </div>
            <span className="large">{text}</span>
            <div className="cell-stepper-icon-wrapper">
              <SvgIcon icon="plus" className="cell-stepper-icon" />
            </div>
          </div>
        )
      case CELL_VARIANT.LINK:
        return (
          <div className="link">
            <a className="lead-source-url large-bold" href={link} target="_blank" rel="noreferrer">
              {link}
            </a>
            <InputButton
              icon="copy-20"
              className="copy-icon"
              onClick={() => {
                navigator.clipboard.writeText(link)
              }}
            />
          </div>
        )

      case CELL_VARIANT.AVATAR:
        return <Avatar name={name} imgSource={imgSource} />
      default:
        return null
    }
  }

  const shouldWrapInPaddingDiv = [
    CELL_VARIANT.TOGGLE,
    CELL_VARIANT.TEXT,
    CELL_VARIANT.STATUS,
    CELL_VARIANT.WEEKDAYS,
    CELL_VARIANT.PROGRESS,
    CELL_VARIANT.STAT,
  ].includes(cellType)

  const shouldWrapInJustifyContent = [CELL_VARIANT.STAT].includes(cellType)

  const tableCellWrapperClass = classNames(`table-cell-wrapper`, {
    "table-cell-wrapper-padding": shouldWrapInPaddingDiv,
    "table-cell-wrapper-justify-content": shouldWrapInJustifyContent,
    [tableCellWrapperClassName]: tableCellWrapperClassName,
  })

  return <td className={tableCellWrapperClass}>{renderInnerContent()}</td>
}

// TableCell.propTypes = {
//   cellType: PropTypes.oneOf(Object.values(CELL_VARIANT)),
//   onChange: PropTypes.func,
//   checkboxIcon: PropTypes.string,
//   isCheckboxChecked: PropTypes.bool,
//   checkboxOnClick: PropTypes.func,
//   isCheckboxDisabled: PropTypes.bool,
//   isRadioButtonChecked: PropTypes.bool,
//   isRadioButtonDisabled: PropTypes.bool,
//   radioButtonName: PropTypes.string,
//   icon: PropTypes.string,
//   switchInitChecked: PropTypes.bool,
//   switchDisabled: PropTypes.bool,
//   text: PropTypes.string,
//   helperText: PropTypes.string,
//   link: PropTypes.string,
//   textCellIcon: PropTypes.string,
//   statColor: PropTypes.string,
//   statusIcon: PropTypes.string,
//   progressPercentage: PropTypes.string,
//   progressTotal: PropTypes.string,
//   weekdayIntervals: PropTypes.instanceOf(Object),
//   setTooltip: PropTypes.func,
//   closeTooltip: PropTypes.func,
//   name: PropTypes.string,
//   imgSource: PropTypes.string,
// }

export default TableCell
